import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AIExpert } from '@garbera/shared';
import { DEFAULT_EXPERTS } from '../types/ai';

export const useExpertAI = () => {
  const [experts, setExperts] = useState<AIExpert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadExperts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // まず公開エンドポイントを試す（認証不要）
      const response = await axios.get('/api/experts/public/active');
      
      const data = response.data.data;
      const expertsList = data.experts || data || [];
      
      // デバッグ用ログ（開発環境のみ）
      if (import.meta.env.DEV) {
        console.log('WelcomeScreen専門家AI取得 (public):', expertsList.map((e: any) => ({ 
          id: e.id, 
          name: e.name, 
          avatarUrl: e.avatarUrl 
        })));
      }
      
      setExperts(expertsList);
    } catch (error: any) {
      // 開発環境でのみログ出力
      if (import.meta.env.DEV) {
        console.error('Failed to load experts from public endpoint:', error);
      }
      
      try {
        // 公開エンドポイントが失敗した場合、認証が必要なエンドポイントを試す
        const response = await axios.get('/api/experts', {
          params: {
            isActive: true,
          }
        });
        
        const data = response.data.data;
        const expertsList = data.experts || data || [];
        
        // デバッグ用ログ（開発環境のみ）
        if (import.meta.env.DEV) {
          console.log('WelcomeScreen専門家AI取得 (authenticated):', expertsList.map((e: any) => ({ 
            id: e.id, 
            name: e.name, 
            avatarUrl: e.avatarUrl 
          })));
        }
        
        setExperts(expertsList);
      } catch (authError: any) {
        // 開発環境でのみログ出力
        if (import.meta.env.DEV) {
          console.error('Failed to load experts from authenticated endpoint:', authError);
        }
        
        // 両方のエンドポイントが失敗した場合、フォールバックデータを使用
        const fallbackExperts: AIExpert[] = DEFAULT_EXPERTS.map((expert, index) => ({
          ...expert,
          id: index + 1,
          createdBy: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
        setExperts(fallbackExperts);
        
        // ユーザー向けエラーは本番環境でも表示
        setError('サーバーから専門家AI一覧を取得できませんでした。フォールバックデータを使用しています。');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    loadExperts();
  }, [loadExperts]);

  return {
    experts,
    isLoading,
    error,
    loadExperts,
    clearError,
  };
}; 